import { useCallback, useState } from 'react';

import { logger, LogType } from 'core/atoms/housekeeping';
import {
  OasPageError,
  OasError,
  OasErrorInterface,
  OasMemoryError,
} from 'core/atoms/errors';
import { parseTemplate } from 'core/atoms/functions/string';
import { useSnackbar } from 'core/cells/notistack';
import { useTranslations } from 'core/dna/translations';
import {
  CreateEvaluationBatchInput,
  UpdateEvaluationBatchInput,
} from 'core/dna/types/generated/_globalTypes';

import {
  useUpdateEvaluationBatch,
  useCreateEvaluationBatch,
} from '../memory/apollo/evaluation/remote';

const TITLE = 'use-batch-table-write';

export const useBatchTableWrite = () => {
  const { texts } = useTranslations();
  const { enqueueSnackbar } = useSnackbar();
  const { createEvaluationBatch } = useCreateEvaluationBatch();
  const { updateEvaluationBatch } = useUpdateEvaluationBatch();

  const [writing, setWriting] = useState(false);

  const onRowAdd = useCallback(
    async (item: CreateEvaluationBatchInput) => {
      try {
        setWriting(true);
        const result = await createEvaluationBatch(item);
        setWriting(false);
        enqueueSnackbar(
          parseTemplate(texts.oasCommon.entityCreated, {
            entityType: texts.grades.prepareBatch.sidebar,
          }),
          {
            variant: 'success',
          },
        );
        return result;
      } catch (e: any) {
        setWriting(false);
        let error: OasErrorInterface;
        if (e instanceof OasError) {
          error = e;
        } else {
          error = OasMemoryError.fromError(e, {
            title: TITLE,
          });
        }
        enqueueSnackbar(error.message, { variant: 'error' });
        throw error;
      }
    },
    [createEvaluationBatch, enqueueSnackbar, texts],
  );

  const onRowUpdate = useCallback(
    async (item: UpdateEvaluationBatchInput) => {
      try {
        setWriting(true);
        const result = await updateEvaluationBatch(item);
        setWriting(false);
        enqueueSnackbar(
          parseTemplate(texts.oasCommon.entityUpdated, {
            entityType: texts.grades.prepareBatch.sidebar,
          }),
          {
            variant: 'success',
          },
        );
        return result;
      } catch (e: any) {
        setWriting(false);
        let error: OasErrorInterface;
        if (e instanceof OasError) {
          error = e;
        } else {
          error = OasMemoryError.fromError(e, {
            title: TITLE,
          });
        }
        enqueueSnackbar(error.message, { variant: 'error' });
        throw error;
      }
    },
    [enqueueSnackbar, updateEvaluationBatch, texts],
  );

  const onRowDelete = useCallback(
    async (oldData: UpdateEvaluationBatchInput) => {
      try {
        setWriting(true);
        enqueueSnackbar('Not implemented!', { variant: 'warning' });
        // await deleteSubstituteJob(oldData.id);
        setWriting(false);
      } catch (e: any) {
        setWriting(false);
        const error =
          e instanceof OasError
            ? e
            : OasPageError.fromError(e, { title: TITLE });
        if (error.originalMessage) {
          logger.info(error.originalMessage, LogType.Error);
        }
        enqueueSnackbar(
          parseTemplate(texts.oasCommon.deleteEntitiesOfTypeError.one, {
            entityType: texts.oasCommon.job.one,
            entity: oldData.term.slice(0, 10),
          }),
          {
            variant: 'error',
          },
        );
        return 1;
      }
    },
    [enqueueSnackbar, texts],
  );

  return {
    onRowAdd,
    onRowUpdate,
    onRowDelete,
    writing,
  };
};
